import React from 'react';

export interface BadgeProps {
  variant?: 'success' | 'warning' | 'error' | 'neutral' | 'neutral-gray' | 'primary' | 'primary-light' | 'purple' | 'dark';
  size?: 'micro' | 'sm' | 'md';
  shape?: 'square' | 'pill';
  children: React.ReactNode;
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  size = 'sm',
  shape = 'square',
  children,
  className = '',
}) => {
  const baseStyles = 'inline-flex items-center font-normal';

  const variantStyles = {
    success: 'bg-emerald-50/80 text-emerald-600',
    warning: 'bg-amber-50/80 text-amber-600',
    error: 'bg-rose-50/80 text-rose-500',
    neutral: 'bg-neutral-100/80 text-neutral-500',
    'neutral-gray': 'bg-neutral-100/80 text-neutral-500',
    primary: 'bg-blue-600 text-white',
    'primary-light': 'bg-blue-50/80 text-blue-500',
    purple: 'bg-purple-100 text-purple-600',
    dark: 'bg-neutral-900 text-white',
  };

  // Size styles - matching actual platform usage
  const sizeStyles = {
    micro: 'px-2 py-0.5 text-[10px]',    // For ID badges, micro indicators
    sm: 'px-2 py-0.5 text-xs',           // Most common size
    md: 'px-2.5 py-0.5 text-xs',         // Status badges (slightly more padding)
  };

  // Shape styles
  const shapeStyles = {
    square: 'rounded',
    pill: 'rounded-full',
  };

  const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${shapeStyles[shape]} ${className}`.trim();

  return <span className={combinedClassName}>{children}</span>;
};

Badge.displayName = 'Badge';
